import { $lsCache } from './$lsCache'
import { $axFrontendHelper } from './$axFrontendHelper'
import { $appHook } from '@shared/model/$appHook'
import { $AxE2AppHooks } from './$AxE2AppHooks'
import { axApiPaths } from '@/AxApiPaths'
import Vue from 'vue'
import { $cart } from './$cart'
import { getCurrentUserProfileData } from '../models/userProfile'
import { cecPaths } from '@/cecSitePath'
import { $Redirecter } from '@/services/redirecter'
import { $toast } from '@/services/$toast'

const axTokenLocalStorageKey = 'AxE2-AuthTokenLocalStorageKey'
const loggedInInThePastLocalStorageKey = 'AxE2-loggedInInThePast'

interface AxSignInResponse {
  access_token: string
  expires_in: number
  refresh_token: string
  user: {
    id: string
  }
}
export interface AxTokenRefreshResponse {
  access_token: string
  expires_in: number
  refresh_token: string
}

interface AxLocalSavedAuthData {
  access_token: string
  expires_in: number
  refresh_token: string
  user_id: string
  //cloudKMemberId: string
  expireAt: number // new Date().getTime + (expires_in * 1000)
  cart_code: string
}

const axSignInStrategies = {
  emailAndPassword: async ({ email, password }): Promise<AxSignInResponse> => {
    const result = await $axFrontendHelper.request<AxSignInResponse>({
      path: axApiPaths.auth.users.authorize,
      data: {
        query: {
          user: {
            email,
            password,
          },
        },
      },
    })
    return result.result
  },
}

/**
 * リフレッシュトークンの保持期限: 30分
 */
const authTokenTTLSec = 60 * 30 // 30 minutes

const emptyAuthState: AxLocalSavedAuthData = {
  access_token: null,
  expires_in: null,
  refresh_token: null,
  user_id: null,
  expireAt: null,
  cart_code: null,
}
const bodyClassNames = {
  signedIn: 'AxE2-auth-signed-in',
  signedOut: 'AxE2-auth-signed-out',
}
/**
 * 認証の状態を管理する。 $axAuthState でsingleton exported
 * 主に、ログインの挙動, およびログインしている際のtokenの管理などを受け持つ
 */
export class AxAuthStateManager {
  auth: AxLocalSavedAuthData

  constructor() {
    this.currentUserAuth
  }

  get user_id() {
    // alias
    return this.userId
  }

  get userId() {
    // alias
    return this.auth && this.auth.user_id && ['null', 'undefined'].indexOf(this.auth.user_id) === -1
      ? this.auth.user_id
      : ''
  }
  /*
  get cloudKMemberId() {
    return this.auth && this.auth.cloudKMemberId && ['null', 'undefined'].indexOf(this.auth.cloudKMemberId) === -1
      ? this.auth.cloudKMemberId
      : ''
  }
*/
  get isLoggedIn(): boolean {
    const token = $lsCache.get(axTokenLocalStorageKey) || emptyAuthState
    if (!token || !token.user_id) {
      return false
    }
    return !!token.user_id
  }

  get currentUserAuth(): AxLocalSavedAuthData {
    const auth = $lsCache.get(axTokenLocalStorageKey) || emptyAuthState
    Vue.set(this, 'auth', auth)
    return this.auth
  }

  set cart_code(str: string) {
    const auth = $axAuthState.currentUserAuth
    $axAuthState._saveAuthToken({ ...auth, cart_code: str })
  }

  get cart_code() {
    const auth = $axAuthState.currentUserAuth
    if (!auth?.cart_code) {
      // 取得できない場合は生成
      const str = new Date().getTime().toString()
      $axAuthState.cart_code = str
      console.log(`cart_code: ${$axAuthState.cart_code}`)
      return str
    }
    console.log(`cart_code: ${auth.cart_code}`)
    return auth.cart_code
  }

  /**
   * サインイン, 現時点では方式は email & password のみ。
   * @param args
   * @param strategy
   */
  async signIn(args: { email; password }, strategy = 'emailAndPassword', useHook = true): Promise<boolean> {
    if (useHook) {
      await $appHook.callHooks($AxE2AppHooks.signIn.BEFORE, args, this)
    }
    try {
      const res: AxSignInResponse = await axSignInStrategies[strategy](args)
      this._saveAuthToken({ ...res, user_id: res.user.id /*, cloudKMemberId: null*/ })
      /*
      const userData = await getCurrentUserProfileData()
      this._saveAuthToken({ ...res, user_id: res.user.id, cloudKMemberId: userData.custom.cloudk_memberid })
      */
      this.updateBodyClass()
      if (useHook) {
        await $appHook.callHooks($AxE2AppHooks.signIn.AFTER, res, this)
      }
      return true
    } catch (e) {
      if (useHook) {
        const errorCode = JSON.parse({ e }.e.response.data.message).errors[0].code
        if (errorCode === '001-003') {
          await $appHook.callHooks($AxE2AppHooks.signinLock, e, this, args)
        } else {
          await $appHook.callHooks($AxE2AppHooks.signIn.ERROR, e, this, args)
        }
      }
      return false
    }
  }

  /**
   * 現在のAxUserTokenを取得する, 有効期限付き
   * ログインしていれば、Tokenが返ってくる
   */
  async currentAxUserAccessToken(): Promise<string> {
    const token = this.currentUserAuth
    if (!token || !token.access_token) {
      return ''
    }
    if (token.expireAt >= $lsCache._now) {
      return token.access_token
    }
    // 有効期限切れのTokenRefresh対応
    try {
      const newToken = await this._refreshAxUserToken(token)
      if (!newToken) {
        return ''
      }
      return newToken.access_token
    } catch (e) {
      return ''
    }
  }

  /**
   * アクセストークンをリフレッシュ with using auth.refresh_token
   * 失敗した場合には、ログアウト状態に遷移する
   * @param user_id
   * @param refresh_token
   * @private
   */
  async _refreshAxUserToken({ user_id, refresh_token /*, cloudKMemberId*/ }): Promise<AxLocalSavedAuthData | ''> {
    try {
      const result = await $axFrontendHelper.request<AxTokenRefreshResponse>({
        path: axApiPaths.auth.users.token_refresh,
        data: {
          query: {
            user_id,
            refresh_token,
          },
        },
      })
      const auth = result.result
      this._saveAuthToken({ ...auth, user_id /*, cloudKMemberId*/ })
    } catch (e) {
      // Token refresh に失敗している = 認証状態が不正なので、クリアする
      // Token削除
      $lsCache.clear(axTokenLocalStorageKey)
      this.updateBodyClass()
    }
    return this.currentUserAuth
  }

  /**
   * アクセストークンを恣意的にリフレッシュ
   */
  async regenerateAxUserToken() {
    const token = this.currentUserAuth
    if (token) {
      this._refreshAxUserToken(token)
    }
  }

  /**
   * 認証成功後にTokenを保存しておく。
   * access_token の有効期限切れのTimestampを計算して保持しておく。
   * AxAPIからのレスポンス expires_in は (何故か) 秒数で帰ってくるので、タイムスタンプに置き換えてから保存しておく。
   * @param access_token
   * @param expires_in
   * @param refresh_token
   * @param user_id
   * @private
   */
  _saveAuthToken({ access_token, expires_in, refresh_token, user_id, cart_code = null /*, cloudKMemberId*/ }) {
    // 60秒ほど余裕を持って設定
    const expireAt = new Date().getTime() + (Number(expires_in) - 60) * 1000
    const token: AxLocalSavedAuthData = {
      access_token,
      expires_in,
      refresh_token,
      user_id,
      expireAt,
      cart_code: cart_code || this.cart_code,
      //cloudKMemberId: cloudKMemberId,
    }
    Vue.set(this, 'auth', token)
    $lsCache.set(axTokenLocalStorageKey, token, authTokenTTLSec)
    this.updateBodyClass()
  }

  /**
   * サインアウトする。
   * colckeiに保存していたAuthTokenを削除し、更にそのKeyをRemote側でもInvokeする (非同期, non-await)
   */
  async signOut() {
    // ログイン状態チェック
    if (!$axAuthState.isLoggedIn) {
      $toast.error('ログインを行ってから再度やり直してください。')
      // 自動でログイン画面へリダイレクトする
      $Redirecter.openSite(cecPaths.accounts.login, false)
    }
    const token = this.currentUserAuth
    if (token && token.access_token) {
      // Tokenを無効化する
      try {
        await $axFrontendHelper.request<AxSignInResponse>({
          path: axApiPaths.auth.users.token_revoke,
          data: {
            query: {
              user_id: token.user_id,
              refresh_tokens: [token.refresh_token],
            },
          },
        })
      } catch (e) {
        // 必要であればハンドリングを入れてください
      }
      // カートリセット

    }
    // Token削除
    $lsCache.clear(axTokenLocalStorageKey)
    $cart.cleanupState()
    Vue.set(this, 'auth', {})
    this.updateBodyClass()
    //ここにログイン画面への遷移を入れる
    $Redirecter.openSite(window.location.pathname, false)
    return
  }

  /**
   * ログイン中のユーザのプロフィールを取得する from API
   */
  async getCurrentLoggedInUserProfile() {
    if (!this.isLoggedIn) {
      return null
    }
    return getCurrentUserProfileData()
  }

  /**
   * パスワードリセットのリクエストを行う
   */
  async passwordReset(data) {
    await $appHook.callHooks($AxE2AppHooks.passwordReset.BEFORE)
    const pathKey = !!data.token ? 'reset' : 'send'
    try {
      await $axFrontendHelper.request<{ result: object }>({
        path: axApiPaths.users.password.reset[pathKey],
        data: {
          query: data,
        },
      })
    } catch (e) {
      throw e
    }
    await $appHook.callHooks($AxE2AppHooks.passwordReset.AFTER, pathKey)
  }

  /**
   * パスワード再設定のリクエストを行う
   */
  async changePassword(data) {
    // ログイン状態チェック
    if (!$axAuthState.isLoggedIn) {
      $toast.error('ログインを行ってから再度やり直してください。')
      // 自動でログイン画面へリダイレクトする
      $Redirecter.openSite(cecPaths.accounts.login, false)
    }
    try {
      await $axFrontendHelper.request<{ result: object }>({
        path: axApiPaths.users.password.put,
        data: {
          query: data,
        },
      })
    } catch (e) {
      // 必要であればハンドリングを入れてください
    }
  }

  /**
   * 退会処理を行う
   */
  async leave(password) {
    await $appHook.callHooks($AxE2AppHooks.user.leave.BEFORE)
    // ログイン状態チェック
    if (!$axAuthState.isLoggedIn) {
      $toast.error('ログインを行ってから再度やり直してください。')
      // 自動でログイン画面へリダイレクトする
      $Redirecter.openSite(cecPaths.accounts.login, false)
      return
    }
    try {
      await $axFrontendHelper.request({
        path: axApiPaths.users.leave,
        data: {
          query: {
            'user.password': password,
          },
        },
      })
      // 退会したのでトークン削除
      $lsCache.clear(axTokenLocalStorageKey)
      await $appHook.callHooks($AxE2AppHooks.user.leave.AFTER)
      //ここにトップ画面への遷移を入れる
      $Redirecter.openSite(cecPaths.index, false)
    } catch (e) {
      await $appHook.callHooks($AxE2AppHooks.user.leave.ERROR)
    }
  }

  /**
   * ログイン状態に応じたbody class の付与
   */
  updateBodyClass() {
    const bodyClassList = window.document.body.classList
    if (this.isLoggedIn) {
      bodyClassList.add(bodyClassNames.signedIn)
      bodyClassList.remove(bodyClassNames.signedOut)
    } else {
      bodyClassList.remove(bodyClassNames.signedIn)
      bodyClassList.add(bodyClassNames.signedOut)
    }
  }
}

export const $axAuthState = new AxAuthStateManager()
$appHook.registerHook('AxE2.AxE2BaseComponent.mounted.AFTER', '$axAuthState.updateBodyClass', () =>
  $axAuthState.updateBodyClass()
)
